import { type PipelineBatchResult } from '../../../../../../governance'
import {
  ALL_QUESTIONS_CORE,
  MODULES,
  MODULE_CODES,
  type Question,
} from '../../../../../../content'
import { resolveModuleCode } from './resolveModuleCode'

export interface ModuleHealth {
  code: string
  name: string
  total: number
  pass: number
  pct: number
  bgClass: string
}

export interface FamilyHealth {
  familyId: string
  total: number
  pass: number
  pct: number
}

export interface ModuleAndFamilyHealthResult {
  moduleHealth: ModuleHealth[]
  familyHealthByModule: Record<string, FamilyHealth[]>
  questionTypeDistribution: Map<string, number>
  visualTypeDistribution: Map<string, number>
  uniqueFamiliesCount: number
}

const EMPTY: ModuleAndFamilyHealthResult = {
  moduleHealth: [],
  familyHealthByModule: {},
  questionTypeDistribution: new Map(),
  visualTypeDistribution: new Map(),
  uniqueFamiliesCount: 0,
}

/**
 * Compute module-level + family-level health, plus the question-type and
 * visual-aid distributions used by the System Coverage card.
 *
 * Single pass over `batchResult.results`; visual aids are tallied from the
 * original question objects (supportAids + stimulusAids).
 *
 * `bgClass` is intentionally left empty here — the UI fills it in from
 * MODULE_CONFIG so the data layer stays presentation-agnostic.
 */
export function computeModuleAndFamilyHealth(
  batchResult: PipelineBatchResult | null,
): ModuleAndFamilyHealthResult {
  if (!batchResult) return EMPTY

  const questionLookup = new Map<string, Question>(
    ALL_QUESTIONS_CORE.map((q) => [q.id, q]),
  )

  const moduleStats = new Map<string, { total: number; pass: number }>()
  const familyStats = new Map<
    string,
    Map<string, { total: number; pass: number }>
  >()
  const qTypeDist = new Map<string, number>()
  const vTypeDist = new Map<string, number>()
  const uniqueFamilies = new Set<string>()

  // Seed all known modules so empty modules still appear with 0/0
  MODULES.forEach((m) => {
    const code = MODULE_CODES[m.id] || m.id
    moduleStats.set(code, { total: 0, pass: 0 })
    familyStats.set(code, new Map())
  })

  for (const r of batchResult.results) {
    const question = questionLookup.get(r.questionId)
    const moduleCode = resolveModuleCode(question, r.questionId)
    const familyId = question?.familyId || 'UNKNOWN'

    const currentMod = moduleStats.get(moduleCode) || { total: 0, pass: 0 }
    moduleStats.set(moduleCode, {
      total: currentMod.total + 1,
      pass: currentMod.pass + (r.gate.verdict === 'PASS' ? 1 : 0),
    })

    if (!familyStats.has(moduleCode)) {
      familyStats.set(moduleCode, new Map())
    }
    const modFamilies = familyStats.get(moduleCode)!
    const currentFam = modFamilies.get(familyId) || { total: 0, pass: 0 }
    modFamilies.set(familyId, {
      total: currentFam.total + 1,
      pass: currentFam.pass + (r.gate.verdict === 'PASS' ? 1 : 0),
    })

    uniqueFamilies.add(familyId)

    if (question) {
      qTypeDist.set(question.type, (qTypeDist.get(question.type) || 0) + 1)
      const allAids = [
        ...(question.supportAids || []),
        ...(question.stimulusAids || []),
      ]
      for (const aid of allAids) {
        if (aid.type) {
          vTypeDist.set(aid.type, (vTypeDist.get(aid.type) || 0) + 1)
        }
      }
    }
  }

  const codeToName = new Map<string, string>()
  MODULES.forEach((m) => {
    const code = MODULE_CODES[m.id] || m.id
    codeToName.set(code, m.title || code)
  })

  const moduleHealth: ModuleHealth[] = Array.from(moduleStats.entries())
    .filter(([, stats]) => stats.total > 0)
    .map(([code, stats]) => ({
      code,
      name: codeToName.get(code) || code,
      total: stats.total,
      pass: stats.pass,
      pct: stats.total > 0 ? Math.round((stats.pass / stats.total) * 100) : 0,
      bgClass: '', // UI fills via MODULE_CONFIG
    }))
    .sort((a, b) => b.total - a.total)

  const familyHealthByModule: Record<string, FamilyHealth[]> = {}
  familyStats.forEach((families, modCode) => {
    familyHealthByModule[modCode] = Array.from(families.entries())
      .map(([familyId, stats]) => ({
        familyId,
        total: stats.total,
        pass: stats.pass,
        pct: stats.total > 0 ? Math.round((stats.pass / stats.total) * 100) : 0,
      }))
      .sort((a, b) => a.familyId.localeCompare(b.familyId))
  })

  return {
    moduleHealth,
    familyHealthByModule,
    questionTypeDistribution: qTypeDist,
    visualTypeDistribution: vTypeDist,
    uniqueFamiliesCount: uniqueFamilies.size,
  }
}
